import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from "recharts";
import {
  TrendingUp,
  Users,
  Shield,
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileText,
  ClipboardCheck,
  BookOpen,
  Download,
  RefreshCw,
  Target,
  Activity,
  FileDown,
  Loader2,
  Library,
  Crown,
  Layers,
  Eye,
  Sparkles,
  BarChart3,
} from "lucide-react";
import ReportInsightsViewer from "../components/ReportInsightsViewer";
import CrossStandardMapping from "../components/CrossStandardMapping";
import AIReportGenerator from "../components/AIReportGenerator";

const COLORS = {
  primary: "#4f46e5",
  success: "#10b981",
  warning: "#f59e0b",
  danger: "#ef4444",
  info: "#3b82f6",
  purple: "#8b5cf6",
  slate: "#64748b",
};

const PIE_COLORS = [COLORS.success, COLORS.warning, COLORS.danger, COLORS.info, COLORS.purple];

export default function ReportsPage() {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [exerciseStats, setExerciseStats] = useState(null);
  const [policyStats, setPolicyStats] = useState(null);
  const [trainingStats, setTrainingStats] = useState(null);
  const [complianceData, setComplianceData] = useState(null);
  const [kpiSummary, setKpiSummary] = useState(null);
  const [dateRange, setDateRange] = useState("30d");
  const [reportIndex, setReportIndex] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [reportFilter, setReportFilter] = useState("all");
  const [viewingReport, setViewingReport] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");

  useEffect(() => {
    fetchAllData();
  }, [dateRange]);

  useEffect(() => {
    fetchReportIndex();
  }, []);

  const fetchReportIndex = async () => {
    setReportsLoading(true);
    try {
      const res = await api.get("/export/reports/index");
      setReportIndex(res.data.reports || []);
    } catch (err) {
      console.error("Failed to load report index:", err);
    } finally {
      setReportsLoading(false);
    }
  };

  const handleDownloadReport = async (reportId, title) => {
    setDownloadingId(reportId);
    try {
      const res = await api.get(`/export/reports/${reportId}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Meridian_${title.replace(/[^a-zA-Z0-9]/g, "_")}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded: ${title}`);
    } catch (err) {
      console.error("PDF download failed:", err);
      toast.error("Failed to download report");
    } finally {
      setDownloadingId(null);
    }
  };

  const filteredReports = reportIndex.filter(r => {
    if (reportFilter === "all") return true;
    if (reportFilter === "tier1") return r.tier === "Tier 1";
    if (reportFilter === "tier2") return r.tier === "Tier 2";
    if (reportFilter === "executive") return r.category === "Executive";
    if (reportFilter === "operational") return r.category === "Operational";
    return true;
  });

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, exerciseRes, policyRes, trainingRes, complianceRes, kpiRes] = await Promise.all([
        api.get("/bcm-admin/dashboard/stats"),
        api.get("/exercises/dashboard/stats"),
        api.get("/policies/dashboard/stats"),
        api.get("/training/dashboard/stats"),
        api.get("/compliance/org/summary").catch(() => ({ data: null })),
        api.get("/kpi/summary").catch(() => ({ data: null })),
      ]);
      setKpiSummary(kpiRes.data);
      // Normalise stats → expose counts at BOTH `stats.*.total` (original shape)
      // AND `stats.analysis.*` (legacy shape that some widgets still read).
      const s = statsRes.data || {};
      s.analysis = {
        bia_assessments: s.bia?.total || 0,
        risk_assessments: s.risks?.total || 0,
        high_risks: s.risks?.high_priority || 0,
        plans: s.plans?.total || 0,
        policies: s.policies?.approved || 0,
        exercises: s.exercises?.completed || 0,
      };
      setStats(s);

      // Normalise exercises stats → surface `completed` & `completion_rate` for UI cards
      const ex = exerciseRes.data || {};
      if (ex.completed == null) ex.completed = ex.completed_count || 0;
      if (ex.completion_rate == null) {
        ex.completion_rate = ex.total_exercises > 0
          ? Math.round((ex.completed / ex.total_exercises) * 100)
          : 0;
      }
      setExerciseStats(ex);

      setPolicyStats(policyRes.data);
      setTrainingStats(trainingRes.data);
      setComplianceData(complianceRes.data);
    } catch (error) {
      console.error("Failed to fetch reports data:", error);
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  // Prepare chart data
  const getProgramMaturityData = () => {
    if (!stats || !exerciseStats || !policyStats || !trainingStats) return [];
    
    return [
      { subject: "Policies", A: policyStats.coverage_score || 0, fullMark: 100 },
      { subject: "Training", A: trainingStats.compliance_rate || 0, fullMark: 100 },
      { subject: "Exercises", A: exerciseStats.completion_rate || 0, fullMark: 100 },
      { subject: "Risk Mgmt", A: stats.analysis?.risk_assessments > 0 ? 75 : 0, fullMark: 100 },
      { subject: "BIA", A: stats.analysis?.bia_assessments > 0 ? 80 : 0, fullMark: 100 },
      { subject: "Plans", A: stats.analysis?.plans > 0 ? 85 : 0, fullMark: 100 },
    ];
  };

  const getExerciseTypeData = () => {
    if (!exerciseStats?.by_type) return [];
    
    return Object.entries(exerciseStats.by_type).map(([type, count]) => ({
      name: type.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase()),
      value: count,
    })).filter(d => d.value > 0);
  };

  const getPolicyStatusData = () => {
    if (!policyStats) return [];
    
    return [
      { name: "Approved", value: policyStats.approved || 0 },
      { name: "Under Review", value: policyStats.under_review || 0 },
      { name: "Draft", value: policyStats.draft || 0 },
    ].filter(d => d.value > 0);
  };

  const getTrainingTypeData = () => {
    if (!trainingStats?.by_type) return [];
    
    const typeLabels = {
      awareness: "Awareness",
      role_specific: "Role-Specific",
      exercise_briefing: "Exercise Brief",
      specialist: "Specialist",
    };
    
    return Object.entries(trainingStats.by_type).map(([type, count]) => ({
      name: typeLabels[type] || type,
      modules: count,
    }));
  };

  const getComplianceOverviewData = () => {
    if (!complianceData) return [];
    
    return [
      { name: "Compliant", value: complianceData.summary?.compliant || 0 },
      { name: "Due Soon", value: complianceData.summary?.due_soon || 0 },
      { name: "Overdue", value: complianceData.summary?.overdue || 0 },
    ];
  };

  // Overall BCMS score — single source of truth: GET /api/kpi/summary overall_score
  // (same value + measured coverage as the Performance & Compliance headline).
  const kpiMeasured = kpiSummary?.categories
    ? Object.values(kpiSummary.categories).reduce((a, c) => a + (c.measured_count || 0), 0)
    : 0;
  const kpiTotal = kpiSummary?.categories
    ? Object.values(kpiSummary.categories).reduce((a, c) => a + (c.kpi_count || 0), 0)
    : 15;
  const overallScoreRaw = (kpiSummary && kpiSummary.overall_score != null) ? Math.round(kpiSummary.overall_score) : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-sky-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const overallScore = overallScoreRaw;
  const maturityData = getProgramMaturityData();
  const exerciseTypeData = getExerciseTypeData();
  const policyStatusData = getPolicyStatusData();
  const trainingTypeData = getTrainingTypeData();
  const complianceOverview = getComplianceOverviewData();

  return (
    <>
    <div className="space-y-8 animate-fade-in" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">BCM Reports & Intelligence</h1>
          <p className="text-slate-600 mt-1">Programme analytics, cross-standard compliance, and AI-powered reporting</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchAllData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-xl" data-testid="report-tabs">
        {[
          { key: "dashboard", label: "Dashboard & Reports", icon: BarChart3 },
          { key: "cross-standard", label: "Cross-Standard Mapping", icon: Shield },
          { key: "ai-generator", label: "AI Report Generator", icon: Sparkles },
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              data-testid={`tab-${tab.key}`}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all flex-1 justify-center ${
                activeTab === tab.key
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700 hover:bg-white/50"
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === "cross-standard" && <CrossStandardMapping />}
      {activeTab === "ai-generator" && <AIReportGenerator />}
      {activeTab === "dashboard" && (
      <>

      {/* ═══ Reports Library ═══ */}
      <Card data-testid="reports-library-section">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-sky-700 flex items-center justify-center">
                <Library className="w-5 h-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-lg">Executive Reports Library</CardTitle>
                <CardDescription>Download comprehensive PDF reports for your organization</CardDescription>
              </div>
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {[
                { key: "all", label: "All" },
                { key: "tier1", label: "Tier 1" },
                { key: "tier2", label: "Tier 2" },
                { key: "executive", label: "Executive" },
                { key: "operational", label: "Operational" },
              ].map(f => (
                <button
                  key={f.key}
                  data-testid={`filter-${f.key}`}
                  onClick={() => setReportFilter(f.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    reportFilter === f.key
                      ? "bg-sky-700 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {reportsLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-sky-700" />
            </div>
          ) : filteredReports.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm">No reports match this filter.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredReports.map((report) => (
                <div
                  key={report.id}
                  data-testid={`report-card-${report.id}`}
                  className="group relative border border-slate-200 rounded-xl p-5 hover:border-sky-300 hover:shadow-md transition-all duration-200 bg-white"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex gap-2">
                      <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold rounded-full tracking-wide ${
                        report.tier === "Tier 1"
                          ? "bg-sky-100 text-sky-800"
                          : "bg-slate-100 text-slate-600"
                      }`}>
                        {report.tier === "Tier 1" && <Crown className="w-3 h-3 mr-0.5" />}
                        {report.tier}
                      </span>
                      <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                        report.category === "Executive"
                          ? "bg-violet-50 text-violet-700"
                          : "bg-emerald-50 text-emerald-700"
                      }`}>
                        {report.category}
                      </span>
                    </div>
                    {report.pages && (
                      <span className="text-[10px] text-slate-400 font-medium">{report.pages} pages</span>
                    )}
                  </div>
                  <h3 className="font-semibold text-sm text-slate-900 mb-1.5 leading-snug">{report.title}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed mb-4">{report.description}</p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`view-report-${report.id}`}
                      onClick={() => setViewingReport(report)}
                      className="flex-1 border-violet-200 text-violet-700 hover:bg-violet-50 hover:border-violet-300"
                    >
                      <Eye className="w-3.5 h-3.5 mr-1.5" />
                      View Insights
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`download-${report.id}`}
                      disabled={downloadingId === report.id}
                      onClick={() => handleDownloadReport(report.id, report.title)}
                      className="flex-1 border-sky-200 text-sky-700 hover:bg-sky-50 hover:border-sky-300 group-hover:bg-sky-700 group-hover:text-white group-hover:border-sky-700 transition-colors"
                    >
                      {downloadingId === report.id ? (
                        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <FileDown className="w-3.5 h-3.5 mr-1.5" />
                      )}
                      {downloadingId === report.id ? "Generating..." : "Download PDF"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Overall Programme Health Score */}
      <Card className="bg-gradient-to-r from-sky-700 to-teal-600 text-white">
        <CardContent className="p-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h2 className="text-xl font-semibold opacity-90">BCM Programme Health Score</h2>
              <p className="text-sm opacity-75 mt-1">Based on {kpiMeasured} of {kpiTotal} measured indicators</p>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="text-6xl font-bold" data-testid="reports-overall-score">{overallScore === null ? "Not started" : `${overallScore}%`}</div>
                <p className="text-sm opacity-75 mt-1">
                  {overallScore === null ? "Nothing measured yet" : overallScore >= 80 ? "Excellent" : overallScore >= 60 ? "Good" : overallScore >= 40 ? "Fair" : "Needs Work"}
                </p>
              </div>
              <div className="w-32 h-32 rounded-full border-8 border-white/30 flex items-center justify-center">
                <Target className="w-16 h-16 opacity-75" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <Users className="w-6 h-6 mx-auto text-sky-700 mb-2" />
            <p className="text-2xl font-bold text-slate-900">{stats?.users?.total || 0}</p>
            <p className="text-xs text-slate-500">Team Members</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Shield className="w-6 h-6 mx-auto text-purple-600 mb-2" />
            <p className="text-2xl font-bold text-slate-900">{policyStats?.approved || 0}</p>
            <p className="text-xs text-slate-500">Approved Policies</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <BookOpen className="w-6 h-6 mx-auto text-blue-600 mb-2" />
            <p className="text-2xl font-bold text-slate-900">{trainingStats?.compliance_rate || 0}%</p>
            <p className="text-xs text-slate-500">Training Compliant</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <ClipboardCheck className="w-6 h-6 mx-auto text-emerald-600 mb-2" />
            <p className="text-2xl font-bold text-slate-900">{exerciseStats?.completed || 0}</p>
            <p className="text-xs text-slate-500">Exercises Done</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <AlertTriangle className="w-6 h-6 mx-auto text-amber-600 mb-2" />
            <p className="text-2xl font-bold text-slate-900">{stats?.analysis?.high_risks || 0}</p>
            <p className="text-xs text-slate-500">High Risks</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <FileText className="w-6 h-6 mx-auto text-teal-600 mb-2" />
            <p className="text-2xl font-bold text-slate-900">{stats?.analysis?.plans || 0}</p>
            <p className="text-xs text-slate-500">BC Plans</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Programme Maturity Radar */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-sky-700" />
              Programme Maturity
            </CardTitle>
            <CardDescription>Coverage across BCM programme areas</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={maturityData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="subject" tick={{ fontSize: 12 }} />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
                  <Radar
                    name="Maturity %"
                    dataKey="A"
                    stroke={COLORS.primary}
                    fill={COLORS.primary}
                    fillOpacity={0.5}
                  />
                  <Tooltip formatter={(value) => `${value}%`} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Compliance Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              Annual Compliance Status
            </CardTitle>
            <CardDescription>BIA, Risk, and Plan review compliance</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              {complianceOverview.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={complianceOverview}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {complianceOverview.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={index === 0 ? COLORS.success : index === 1 ? COLORS.warning : COLORS.danger} 
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-500">
                  <p>No compliance data available</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Policy Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-600" />
              Policy Status Distribution
            </CardTitle>
            <CardDescription>Breakdown by approval status</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {policyStatusData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={policyStatusData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {policyStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-500">
                  <p>No policies created yet</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Exercise Types */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-emerald-600" />
              Exercise Types
            </CardTitle>
            <CardDescription>Exercises by BCI GPG type</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {exerciseTypeData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={exerciseTypeData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="value" fill={COLORS.primary} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-500">
                  <p>No exercises completed yet</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Training Modules by Type */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-blue-600" />
            Training Programme Overview
          </CardTitle>
          <CardDescription>Training modules by category</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trainingTypeData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="modules" name="Modules" fill={COLORS.info} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* BCI GPG Compliance Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-sky-700" />
            BCI GPG Professional Practices Compliance
          </CardTitle>
          <CardDescription>Status of each Professional Practice area</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { pp: "PP1", name: "Policy & Programme", icon: Shield, status: (policyStats?.approved || 0) > 0, score: policyStats?.coverage_score || 0 },
              { pp: "PP2", name: "Embedding & Training", icon: BookOpen, status: (trainingStats?.total_modules || 0) > 0, score: trainingStats?.compliance_rate || 0 },
              { pp: "PP3", name: "Analysis (BIA & Risk)", icon: AlertTriangle, status: (stats?.analysis?.bia_assessments || 0) > 0, score: (stats?.analysis?.bia_assessments || 0) > 0 ? 75 : 0 },
              { pp: "PP4", name: "Design (Strategy)", icon: Target, status: true, score: 80 },
              { pp: "PP5", name: "Implementation (Plans)", icon: FileText, status: (stats?.analysis?.plans || 0) > 0, score: (stats?.analysis?.plans || 0) > 0 ? 85 : 0 },
              { pp: "PP6", name: "Validation (Exercises)", icon: ClipboardCheck, status: (exerciseStats?.completed || 0) > 0, score: exerciseStats?.completion_rate || 0 },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.pp}
                  className={`p-4 rounded-lg border ${
                    item.status ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Icon className={`w-5 h-5 ${item.status ? "text-emerald-600" : "text-slate-400"}`} />
                      <span className="font-semibold text-slate-900">{item.pp}</span>
                    </div>
                    <span className={`text-lg font-bold ${item.status ? "text-emerald-600" : "text-slate-400"}`}>
                      {item.score}%
                    </span>
                  </div>
                  <p className="text-sm text-slate-600">{item.name}</p>
                  <div className="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${item.status ? "bg-emerald-500" : "bg-slate-300"}`}
                      style={{ width: `${item.score}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Compliance Issues Summary */}
      {complianceData && (complianceData.summary?.overdue > 0 || complianceData.summary?.due_soon > 0) && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <Clock className="w-5 h-5" />
              Compliance Attention Required
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {complianceData.summary?.overdue > 0 && (
                <div className="p-4 bg-red-100 rounded-lg">
                  <p className="text-2xl font-bold text-red-700">{complianceData.summary.overdue}</p>
                  <p className="text-sm text-red-600">Overdue Items</p>
                  <p className="text-xs text-red-500 mt-1">Require immediate attention</p>
                </div>
              )}
              {complianceData.summary?.due_soon > 0 && (
                <div className="p-4 bg-amber-100 rounded-lg">
                  <p className="text-2xl font-bold text-amber-700">{complianceData.summary.due_soon}</p>
                  <p className="text-sm text-amber-600">Due Within 90 Days</p>
                  <p className="text-xs text-amber-500 mt-1">Schedule reviews soon</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Report Insights Viewer */}
      <ReportInsightsViewer
        reportId={viewingReport?.id}
        reportTitle={viewingReport?.title}
        open={!!viewingReport}
        onClose={() => setViewingReport(null)}
      />
      </>
    )}
    </div>
    </>
  );
}
